import {
  AiModelDataType,
  AiModelEnvironment,
  AiModelModality,
  AiModelPayloadFormat,
  AiModelProvider,
  AiModelStatus,
  AiModelTask,
  InferenceMode,
} from './aiModelTypes';

export const AI_MODELS_MODULE_ID = '7c1e9a4b-2f58-4d6c-b3a0-8e5d1f47c920';
export const AI_MODELS_REGISTER_ID = 'a9f3c6e1-4b72-4d8a-9c10-5e8d2f47b1a3';

export const REGISTER_STEPS = [
  'Model Info',
  'Source & Connection',
  'Model Type',
  'Input Contract',
  'Output Contract',
  'Preprocessing',
  'Inference Mode',
  'Examples',
  'Test',
  'Review & Register',
] as const;

export const PROVIDER_OPTIONS: { value: AiModelProvider; label: string }[] = [
  { value: 'sagemaker', label: 'AWS SageMaker' },
  { value: 'azure_ml', label: 'Azure ML' },
  { value: 'vertex_ai', label: 'Vertex AI' },
  { value: 'huggingface', label: 'Hugging Face' },
  { value: 'mlflow', label: 'MLflow' },
  { value: 'databricks', label: 'Databricks' },
  { value: 'rest', label: 'REST' },
  { value: 'on_prem', label: 'On-Prem' },
];

export const PROVIDER_LOCATION_LABEL: Record<AiModelProvider, string> = {
  sagemaker: 'Region',
  azure_ml: 'Workspace',
  vertex_ai: 'Project',
  huggingface: 'Model Hub',
  mlflow: 'Tracking URI',
  databricks: 'Workspace',
  rest: 'Base URL',
  on_prem: 'Host',
};

export const PROVIDER_LOCATION_PLACEHOLDER: Record<AiModelProvider, string> = {
  sagemaker: 'us-east-1',
  azure_ml: 'fraud-ml-workspace',
  vertex_ai: 'acme-ml-prod',
  huggingface: 'huggingface.co',
  mlflow: 'https://mlflow.internal',
  databricks: 'adb-123.azuredatabricks.net',
  rest: 'https://models.internal/v1',
  on_prem: '10.0.12.40:8080',
};

export const TASK_OPTIONS: { value: AiModelTask; label: string }[] = [
  { value: 'binary_classification', label: 'Binary Classification' },
  { value: 'multiclass', label: 'Multiclass' },
  { value: 'regression', label: 'Regression' },
  { value: 'anomaly_detection', label: 'Anomaly Detection' },
  { value: 'clustering', label: 'Clustering' },
  { value: 'forecasting', label: 'Forecasting' },
];

export const MODALITY_OPTIONS: { value: AiModelModality; label: string }[] = [
  { value: 'tabular', label: 'Tabular' },
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Image' },
  { value: 'audio', label: 'Audio' },
  { value: 'multimodal', label: 'Multimodal' },
];

export const ENVIRONMENT_OPTIONS: { value: AiModelEnvironment; label: string }[] = [
  { value: 'development', label: 'Development' },
  { value: 'staging', label: 'Staging' },
  { value: 'production', label: 'Production' },
];

export const STATUS_OPTIONS: { value: AiModelStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'registered', label: 'Registered' },
  { value: 'deprecated', label: 'Deprecated' },
];

export const INFERENCE_MODE_OPTIONS: { value: InferenceMode; label: string; hint: string }[] = [
  { value: 'realtime', label: 'Real-time', hint: 'Synchronous invoke from a workflow step' },
  { value: 'batch', label: 'Batch', hint: 'Score a dataset or file in bulk' },
  { value: 'both', label: 'Both', hint: 'Same endpoint supports real-time and batch' },
];

export const DATA_TYPE_OPTIONS: { value: AiModelDataType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'integer', label: 'Integer' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'category', label: 'Category' },
  { value: 'datetime', label: 'Datetime' },
  { value: 'array', label: 'Array' },
  { value: 'object', label: 'Object' },
];

export const PAYLOAD_FORMAT_OPTIONS: { value: AiModelPayloadFormat; label: string }[] = [
  { value: 'raw', label: 'Raw JSON body' },
  { value: 'sagemaker_instances', label: 'SageMaker instances' },
  { value: 'vertex_instances', label: 'Vertex AI instances' },
  { value: 'dataframe_records', label: 'Databricks dataframe_records' },
];

export const AUTH_SUGGESTIONS = [
  'Connected AWS Account',
  'Connected Azure Workspace',
  'Connected GCP Project',
  'Hugging Face Token',
  'MLflow Service Principal',
  'Databricks PAT',
  'REST Bearer via Connections',
  'On-Prem mTLS',
];

export const CLASSIFICATION_TASKS: AiModelTask[] = ['binary_classification', 'multiclass'];

export function providerLabel(value: AiModelProvider): string {
  return PROVIDER_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
}

export function taskLabel(value: AiModelTask): string {
  return TASK_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
}

export function modalityLabel(value: AiModelModality): string {
  return MODALITY_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
}

export function environmentLabel(value: AiModelEnvironment): string {
  return ENVIRONMENT_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
}

export function statusLabel(value: AiModelStatus): string {
  return STATUS_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
}

export function inferenceModeLabel(value: InferenceMode): string {
  return INFERENCE_MODE_OPTIONS.find((opt) => opt.value === value)?.label ?? value;
}

export function defaultPayloadFormat(provider: AiModelProvider): AiModelPayloadFormat {
  if (provider === 'sagemaker') return 'sagemaker_instances';
  if (provider === 'vertex_ai') return 'vertex_instances';
  if (provider === 'databricks') return 'dataframe_records';
  return 'raw';
}
